import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import API from "../config/API";

function OfflineCashPaymentDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  // =====================================================
  // BOOKING DATA
  // =====================================================

  const booking = location.state?.booking || {};

  // =====================================================
  // USER
  // =====================================================

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // =====================================================
  // PAYMENT AMOUNT
  // =====================================================

  const [paymentAmount, setPaymentAmount] = useState("");

  const [loading, setLoading] = useState(false);

  // =====================================================
  // BED COST
  // =====================================================

  const monthlyRent =
    Number(booking.bedOfferCost || 0) > 0
      ? Number(booking.bedOfferCost)
      : Number(booking.bedCost || 0);

  const dailyRent = Number(booking.dayBedCost || 0);

  // =====================================================
  // DURATION
  // =====================================================

  const duration = Math.max(Number(booking.duration || 1), 1);

  // =====================================================
  // TOTAL BED COST
  // =====================================================

  const totalBedCost = useMemo(() => {
    if (booking.stayingType === "DAY") {
      return dailyRent * duration;
    }

    return monthlyRent * duration;
  }, [booking.stayingType, dailyRent, monthlyRent, duration]);

  // =====================================================
  // PAYMENT AMOUNT
  // =====================================================

  const paidAmount = Number(paymentAmount || 0);

  // =====================================================
  // PENDING AMOUNT
  // =====================================================

  const pendingAmount = Math.max(totalBedCost - paidAmount, 0);

  // =====================================================
  // PAYMENT STATUS
  // =====================================================

  const paymentStatus =
    paidAmount === 0
      ? "PENDING"
      : pendingAmount === 0
        ? "PAID"
        : "PARTIALLY_PAID";

  // =====================================================
  // PAYMENT MODE
  // =====================================================

  const paymentMode = booking.paymentMode || "CASH";

  // =====================================================
  // HANDLE PAYMENT AMOUNT
  // =====================================================

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setPaymentAmount("");
      return;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue) || numericValue < 0) {
      return;
    }

    if (numericValue > totalBedCost) {
      alert(`Payment amount cannot exceed ₹${formatCurrency(totalBedCost)}`);

      setPaymentAmount(totalBedCost);
      return;
    }

    setPaymentAmount(numericValue);
  };

  // =====================================================
  // SUBMIT OFFLINE / CASH PAYMENT
  // =====================================================

  const handleConfirmPayment = async () => {
    const paidAmount = Number(paymentAmount || 0);

    // ---------------------------------------------
    // VALIDATION
    // ---------------------------------------------

    if (!booking.registerId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Registration",
        text: "User registration ID is missing.",
      });
      return;
    }

    if (!booking.bedId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Bed",
        text: "Bed ID is missing.",
      });
      return;
    }

    if (paidAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Enter Payment Amount",
        text: "Please enter the payment amount.",
      });
      return;
    }

    if (paidAmount > totalBedCost) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: `Payment amount cannot exceed ₹${formatCurrency(totalBedCost)}.`,
      });
      return;
    }

    try {
      setLoading(true);

      // ---------------------------------------------
      // PAYMENT STATUS
      // ---------------------------------------------

      const finalPendingAmount = Math.max(totalBedCost - paidAmount, 0);

      const finalPaymentStatus =
        finalPendingAmount === 0 ? "PAID" : "PARTIALLY_PAID";

      // ---------------------------------------------
      // PAYMENT PAYLOAD
      // ---------------------------------------------

      const payload = {
        registerId: Number(booking.registerId),
        bedId: Number(booking.bedId),

        paymentType: booking.stayingType || "MONTH",

        stayingDays: Number(booking.duration || 1),

        amount: Number(paidAmount),

        totalAmount: Number(totalBedCost),

        pendingAmount: Number(finalPendingAmount),

        userBedJoiningDate: booking.userBedJoiningDate
          ? `${booking.userBedJoiningDate}T00:00:00`
          : null,

        paymentMode: booking.paymentMode === "OFFLINE" ? "OFFLINE" : "CASH",

        paymentStatus: finalPaymentStatus,

        createdBy: user?.emailId || booking.createdBy || "",
      };

      console.log("=================================");
      console.log("OFFLINE PAYMENT PAYLOAD");
      console.log("=================================");
      console.log(payload);

      // ---------------------------------------------
      // API CALL
      // ---------------------------------------------

      const response = await axios.post(
        "http://localhost:9777/v2/payment/paymentByOffline",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("=================================");
      console.log("OFFLINE PAYMENT RESPONSE");
      console.log("=================================");
      console.log(response.data);

      // ---------------------------------------------
      // SUCCESS
      // ---------------------------------------------

      if (response?.data?.statusCode === 200) {
        await Swal.fire({
          icon: "success",
          title: "Payment Successful",
          text: response.data.message || "Bed confirmed successfully.",
          confirmButtonText: "OK",
        });

        navigate("/dashboard", {
          state: {
            payment: payload,
            booking: booking,
            response: response.data,
          },
        });

        return;
      }

      // ---------------------------------------------
      // API ERROR
      // ---------------------------------------------

      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: response?.data?.message || "Unable to record payment.",
      });
    } catch (error) {
      console.error("❌ PAYMENT ERROR:", error);

      if (error?.response?.status === 401) {
        await Swal.fire({
          icon: "warning",
          title: "Session Expired",
          text: "Please login again.",
        });

        localStorage.clear();
        navigate("/");

        return;
      }

      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while recording payment.",
      });
    } finally {
      setLoading(false);
    }
  };
  // =====================================================
  // NO BOOKING DATA
  // =====================================================

  if (!booking || !booking.bedId) {
    return (
      <div style={styles.emptyPage}>
        <div style={styles.emptyCard}>
          <div style={styles.emptyIcon}>⚠️</div>

          <h2>Booking Details Not Found</h2>

          <p>We could not find the selected bed booking details.</p>

          <button onClick={() => navigate(-1)} style={styles.backButton}>
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <div style={styles.page}>
      {/* =================================================
          HEADER
      ================================================= */}

      <div style={styles.header}>
        <button style={styles.headerBack} onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div style={styles.headerIcon}>💳</div>

        <div>
          <h1 style={styles.title}>Offline Payment</h1>

          <p style={styles.subtitle}>
            Review booking details and record payment
          </p>
        </div>
      </div>

      <div style={styles.container}>
        {/* =================================================
            BED DETAILS
        ================================================= */}

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <span style={styles.sectionLabel}>BED INFORMATION</span>

              <h2 style={styles.cardTitle}>🛏️ {booking.bedName || "-"}</h2>
            </div>

            <span style={styles.availableBadge}>AVAILABLE</span>
          </div>

          <div style={styles.grid}>
            <InfoItem label="Floor" value={booking.floorName} />

            <InfoItem label="Room" value={booking.roomName} />

            <InfoItem label="Bed" value={booking.bedName} />

            <InfoItem
              label="Sharing"
              value={
                booking.bedSharingPerRoom
                  ? `${booking.bedSharingPerRoom} Sharing`
                  : "-"
              }
            />
          </div>
        </div>

        {/* =================================================
            CUSTOMER DETAILS
        ================================================= */}

        <div style={styles.card}>
          <span style={styles.sectionLabel}>CUSTOMER INFORMATION</span>

          <h2 style={styles.cardTitle}>👤 Customer</h2>

          <div style={styles.grid}>
            <InfoItem label="Name" value={booking.userName} />

            <InfoItem label="Mobile" value={booking.mobileNumber} />

            <InfoItem label="Email" value={booking.emailId} />

            <InfoItem label="Registration ID" value={booking.registerId} />
          </div>
        </div>

        {/* =================================================
            PAYMENT SUMMARY
        ================================================= */}

        <div style={styles.paymentCard}>
          <div style={styles.cardHeader}>
            <div>
              <span style={styles.sectionLabel}>PAYMENT INFORMATION</span>

              <h2 style={styles.cardTitle}>💰 Payment Summary</h2>
            </div>

            <span style={styles.modeBadge}>
              {paymentMode === "CASH" ? "💵 CASH" : "🏦 OFFLINE"}
            </span>
          </div>

          {/* STAY DETAILS */}

          <div style={styles.stayGrid}>
            <InfoItem
              label="Stay Type"
              value={booking.stayingType === "DAY" ? "Daily" : "Monthly"}
            />

            <InfoItem
              label="Duration"
              value={`${duration} ${
                booking.stayingType === "DAY" ? "Day(s)" : "Month(s)"
              }`}
            />

            <InfoItem
              label="Rent"
              value={`₹ ${formatCurrency(
                booking.stayingType === "DAY" ? dailyRent : monthlyRent,
              )}`}
            />
          </div>

          {/* TOTAL BED COST */}

          <div style={styles.totalAmountBox}>
            <div>
              <span>Total Bed Cost</span>

              <small>{duration} × rent</small>
            </div>

            <strong>₹ {formatCurrency(totalBedCost)}</strong>
          </div>

          {/* PAYMENT AMOUNT */}

          <div style={styles.amountSection}>
            <label style={styles.amountLabel}>💵 Payment Amount</label>

            <div style={styles.amountInputWrapper}>
              <span style={styles.rupee}>₹</span>

              <input
                type="number"
                min="0"
                max={totalBedCost}
                value={paymentAmount}
                onChange={handlePaymentAmountChange}
                placeholder="Enter payment amount"
                style={styles.amountInput}
              />
            </div>

            <small style={styles.helper}>
              Maximum payment: ₹ {formatCurrency(totalBedCost)}
            </small>
          </div>

          {/* PAYMENT CALCULATION */}

          <div style={styles.calculationBox}>
            <div style={styles.row}>
              <span>Total Bed Cost</span>

              <strong>₹ {formatCurrency(totalBedCost)}</strong>
            </div>

            <div style={styles.row}>
              <span>Payment Amount</span>

              <strong
                style={{
                  color: "#16a34a",
                }}
              >
                ₹ {formatCurrency(paidAmount)}
              </strong>
            </div>

            <div style={styles.divider} />

            {/* PENDING AMOUNT */}

            <div style={styles.pendingRow}>
              <span>Pending Amount</span>

              <strong>₹ {formatCurrency(pendingAmount)}</strong>
            </div>
          </div>

          {/* PAYMENT STATUS */}

          <div
            style={{
              ...styles.statusBox,

              background:
                paymentStatus === "PAID"
                  ? "#dcfce7"
                  : paymentStatus === "PARTIALLY_PAID"
                    ? "#fef3c7"
                    : "#fee2e2",

              color:
                paymentStatus === "PAID"
                  ? "#15803d"
                  : paymentStatus === "PARTIALLY_PAID"
                    ? "#92400e"
                    : "#b91c1c",
            }}
          >
            <div style={styles.statusIcon}>
              {paymentStatus === "PAID"
                ? "✓"
                : paymentStatus === "PARTIALLY_PAID"
                  ? "⚠"
                  : "!"}
            </div>

            <div>
              <strong>
                {paymentStatus === "PAID"
                  ? "Payment Fully Paid"
                  : paymentStatus === "PARTIALLY_PAID"
                    ? "Partially Paid"
                    : "Payment Pending"}
              </strong>

              <small>
                {pendingAmount > 0
                  ? `₹ ${formatCurrency(pendingAmount)} remaining`
                  : "No pending amount"}
              </small>
            </div>
          </div>
        </div>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <div style={styles.actions}>
          <button
            type="button"
            disabled={loading}
            onClick={() => navigate(-1)}
            style={styles.cancelButton}
          >
            ← Back
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={handleConfirmPayment}
            style={{
              ...styles.confirmButton,

              opacity: loading ? 0.6 : 1,

              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Saving Payment..." : "✓ Confirm Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// INFO ITEM
// =====================================================

function InfoItem({ label, value }) {
  return (
    <div style={styles.infoItem}>
      <span>{label}</span>

      <strong>{value || "-"}</strong>
    </div>
  );
}

// =====================================================
// CURRENCY
// =====================================================

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

// =====================================================
// STYLES
// =====================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#f8fafc,#eef2ff)",
    padding: "30px 20px",
    fontFamily: "'Segoe UI',Arial,sans-serif",
    boxSizing: "border-box",
  },

  header: {
    maxWidth: "1050px",
    margin: "0 auto 25px",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },

  headerBack: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
  },

  headerIcon: {
    width: "58px",
    height: "58px",
    borderRadius: "15px",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "28px",
  },

  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "29px",
  },

  subtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "14px",
  },

  container: {
    maxWidth: "1050px",
    margin: "auto",
  },

  card: {
    background: "#fff",
    borderRadius: "18px",
    padding: "24px",
    marginBottom: "18px",
    boxShadow: "0 8px 25px rgba(15,23,42,.07)",
    border: "1px solid #e2e8f0",
  },

  paymentCard: {
    background: "#fff",
    borderRadius: "20px",
    padding: "25px",
    marginBottom: "18px",
    boxShadow: "0 10px 30px rgba(37,99,235,.09)",
    border: "1px solid #dbeafe",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  sectionLabel: {
    display: "block",
    fontSize: "11px",
    fontWeight: "800",
    letterSpacing: "1px",
    color: "#94a3b8",
    marginBottom: "4px",
  },

  cardTitle: {
    margin: 0,
    fontSize: "21px",
    color: "#0f172a",
  },

  availableBadge: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "7px 12px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "800",
  },

  modeBadge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "8px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "800",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: "12px",
  },

  stayGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
    gap: "12px",
    marginBottom: "20px",
  },

  infoItem: {
    background: "#f8fafc",
    borderRadius: "12px",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },

  totalAmountBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderRadius: "15px",
    background: "linear-gradient(135deg,#eff6ff,#eef2ff)",
    border: "1px solid #dbeafe",
    marginBottom: "22px",
  },

  amountSection: {
    marginBottom: "20px",
  },

  amountLabel: {
    display: "block",
    color: "#334155",
    fontWeight: "700",
    marginBottom: "8px",
  },

  amountInputWrapper: {
    display: "flex",
    alignItems: "center",
    border: "2px solid #bfdbfe",
    borderRadius: "13px",
    background: "#fff",
  },

  rupee: {
    paddingLeft: "16px",
    color: "#2563eb",
    fontSize: "20px",
    fontWeight: "800",
  },

  amountInput: {
    width: "100%",
    border: "none",
    outline: "none",
    padding: "15px",
    fontSize: "20px",
    fontWeight: "700",
    boxSizing: "border-box",
  },

  helper: {
    display: "block",
    color: "#94a3b8",
    marginTop: "5px",
    fontSize: "12px",
  },

  calculationBox: {
    background: "#f8fafc",
    borderRadius: "15px",
    padding: "18px",
    border: "1px solid #e2e8f0",
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    color: "#64748b",
  },

  divider: {
    borderTop: "1px solid #e2e8f0",
    margin: "8px 0",
  },

  pendingRow: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "10px",
    color: "#dc2626",
    fontSize: "17px",
  },

  statusBox: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "18px",
    padding: "15px",
    borderRadius: "13px",
  },

  statusIcon: {
    width: "35px",
    height: "35px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,.6)",
    fontWeight: "800",
  },

  actions: {
    display: "flex",
    justifyContent: "space-between",
    gap: "15px",
    marginTop: "20px",
  },

  cancelButton: {
    padding: "14px 25px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    borderRadius: "11px",
    fontWeight: "700",
    cursor: "pointer",
  },

  confirmButton: {
    flex: 1,
    maxWidth: "350px",
    padding: "14px 25px",
    border: "none",
    borderRadius: "11px",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    color: "#fff",
    fontWeight: "800",
    fontSize: "15px",
    boxShadow: "0 8px 20px rgba(37,99,235,.25)",
  },

  emptyPage: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f8fafc",
    padding: "20px",
  },

  emptyCard: {
    background: "#fff",
    padding: "40px",
    borderRadius: "20px",
    textAlign: "center",
    maxWidth: "400px",
    boxShadow: "0 10px 30px rgba(0,0,0,.08)",
  },

  emptyIcon: {
    fontSize: "50px",
  },

  backButton: {
    marginTop: "15px",
    padding: "12px 22px",
    border: "none",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
  },
};

export default OfflineCashPaymentDetails;
